import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap";
import { RefreshCw } from "react-feather";
import { toast } from "react-toastify";
import { partnerService } from "../../../services/partnerService";
import LicenseModal from "../LicenseModal";
import PartnerFormFields from "./PartnerFormFields";
import LicensesTable from "./LicensesTable";
import PartnerInfoBlock from "./PartnerInfoBlock";

const PartnerModal = ({
  isOpen,
  toggle,
  mode = "create",
  partnerData = null,
  onSuccess,
  onDelete,
  onToggleActive,
  onSyncPartner,
  onLicenseCreate,
  onLicenseRenew,
  onLicenseDelete,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    token: "",
    startDate: "",
    endDate: "",
    level: "GOLD",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  // Licenses state for view mode
  const [licenses, setLicenses] = useState([]);
  const [loadingLicenses, setLoadingLicenses] = useState(false);

  // License modal state
  const [licenseModalState, setLicenseModalState] = useState({
    isOpen: false,
    mode: "create",
    licenseData: null,
  });

  const fetchLicenses = async () => {
    if (!partnerData?.uuid) return;
    setLoadingLicenses(true);
    try {
      const response = await partnerService.getByUuid(partnerData.uuid, { includeLicenses: true });
      setLicenses(response.licenses || []);
    } catch (error) {
      console.error("Error fetching licenses:", error);
      setLicenses([]);
    } finally {
      setLoadingLicenses(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" || mode === "view") {
        const firstLicense = partnerData?.licenses?.[0] || {};
        setFormData({
          name: partnerData?.name || "",
          token: partnerData?.token || "",
          startDate: firstLicense.startDate || "",
          endDate: firstLicense.endDate || "",
          level: firstLicense.level || "GOLD",
        });
        if (mode === "view") {
          fetchLicenses();
        }
      } else {
        setFormData({
          name: "",
          token: "",
          startDate: "",
          endDate: "",
          level: "GOLD",
        });
      }
      setErrors({});
      setLicenses([]);
    }
  }, [isOpen, mode, partnerData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Partner name is required";
    } else if (formData.name.length < 2) {
      newErrors.name = "Partner name must be at least 2 characters";
    }

    if (!formData.startDate) {
      newErrors.startDate = "Start date is required";
    }

    if (!formData.endDate) {
      newErrors.endDate = "End date is required";
    }

    if (formData.startDate && formData.endDate && formData.startDate >= formData.endDate) {
      newErrors.endDate = "End date must be after start date";
    }

    if (!formData.level) {
      newErrors.level = "License level is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mode === "view") {
      toggle();
      return;
    }

    if (!validateForm()) {
      toast.error("Please fix the form errors");
      return;
    }

    setLoading(true);

    try {
      let response;

      if (mode === "create") {
        const trimmedToken = formData.token.trim();
        const payload = {
          name: formData.name.trim(),
          ...(trimmedToken && { token: trimmedToken }),
          licenses: [
            {
              startDate: formData.startDate,
              endDate: formData.endDate,
              level: formData.level,
            },
          ],
        };
        response = await partnerService.create(payload);
        toast.success("Partner created successfully");
      } else if (mode === "edit") {
        const trimmedToken = formData.token.trim();
        const payload = {
          name: formData.name.trim(),
          token: trimmedToken || null,
        };
        response = await partnerService.update(partnerData.uuid, payload);
        toast.success("Partner updated successfully");
      }

      if (onSuccess) {
        onSuccess(response);
      }

      toggle();
    } catch (error) {
      console.error("Error saving partner:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(`Failed to ${mode === "create" ? "create" : "update"} partner`);
      }
    } finally {
      setLoading(false);
    }
  };

  const getModalTitle = () => {
    switch (mode) {
      case "create": return "Create New Partner";
      case "edit": return "Edit Partner";
      case "view": return "View Partner Details";
      default: return "Partner";
    }
  };

  // License modal handlers
  const openLicenseCreateModal = () => {
    setLicenseModalState({ isOpen: true, mode: "create", licenseData: null });
  };

  const openLicenseRenewModal = (license) => {
    setLicenseModalState({ isOpen: true, mode: "renew", licenseData: license });
  };

  const closeLicenseModal = () => {
    setLicenseModalState({ isOpen: false, mode: "create", licenseData: null });
  };

  const handleLicenseModalSubmit = async (data) => {
    if (licenseModalState.mode === "create") {
      await onLicenseCreate(partnerData, data);
    } else {
      await onLicenseRenew(partnerData, licenseModalState.licenseData.uuid, data);
    }
    fetchLicenses();
  };

  const handleLicenseDeleteClick = async (license) => {
    try {
      await onLicenseDelete(partnerData, license.uuid);
      fetchLicenses();
    } catch (error) {
      console.error("Delete license failed:", error);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>{getModalTitle()}</ModalHeader>
        <>
          <ModalBody>
            <PartnerFormFields
              formData={formData}
              errors={errors}
              onChange={handleChange}
              mode={mode}
              partnerData={partnerData}
            />

            {/* Licenses Section - view mode */}
            {mode === "view" && partnerData && (
              <LicensesTable
                licenses={licenses}
                loading={loadingLicenses}
                actionLoading={actionLoading}
                onCreateClick={onLicenseCreate ? openLicenseCreateModal : null}
                onRenew={onLicenseRenew ? openLicenseRenewModal : null}
                onDelete={onLicenseDelete ? handleLicenseDeleteClick : null}
              />
            )}

            {/* Partner Info - view/edit mode */}
            {(mode === "edit" || mode === "view") && (
              <PartnerInfoBlock partnerData={partnerData} />
            )}
          </ModalBody>

          <ModalFooter>
            {mode === "view" ? (
              <>
                <div className="d-flex gap-2 me-auto">
                  {onDelete && (
                    <Button
                      type="button"
                      color="danger"
                      onClick={async () => {
                        setActionLoading("delete");
                        try { await onDelete(partnerData); } finally { setActionLoading(null); }
                      }}
                      disabled={!!actionLoading}
                      title="Permanently delete this partner"
                    >
                      {actionLoading === "delete" && (
                        <span className="spinner-border spinner-border-sm me-1"></span>
                      )}
                      Delete
                    </Button>
                  )}
                  {onToggleActive && (
                    <Button
                      type="button"
                      color={partnerData?.active !== false ? "warning" : "success"}
                      onClick={async () => {
                        setActionLoading("toggle");
                        try { await onToggleActive(partnerData); } finally { setActionLoading(null); }
                      }}
                      disabled={!!actionLoading}
                      title={partnerData?.active !== false ? "Deactivate this partner" : "Activate this partner"}
                    >
                      {actionLoading === "toggle" && (
                        <span className="spinner-border spinner-border-sm me-1"></span>
                      )}
                      {partnerData?.active !== false ? "Deactivate" : "Activate"}
                    </Button>
                  )}
                  {onSyncPartner && (
                    <Button
                      type="button"
                      color="primary"
                      outline
                      onClick={async () => {
                        setActionLoading("sync");
                        try { await onSyncPartner(partnerData); } finally { setActionLoading(null); }
                      }}
                      disabled={!!actionLoading}
                      title="Re-fetch project and point metadata from E2 Cloud for this partner"
                    >
                      {actionLoading === "sync" ? (
                        <span className="spinner-border spinner-border-sm me-1"></span>
                      ) : (
                        <RefreshCw size={14} className="me-1" />
                      )}
                      {actionLoading === "sync" ? "Syncing..." : "Sync Projects"}
                    </Button>
                  )}
                </div>
                <Button type="button" color="light" onClick={toggle} disabled={!!actionLoading}>
                  Close
                </Button>
              </>
            ) : (
              <>
                <Button type="button" color="secondary" onClick={toggle} disabled={loading}>
                  Cancel
                </Button>
                <Button color="primary" onClick={handleSubmit} disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {mode === "create" ? "Creating..." : "Updating..."}
                    </>
                  ) : mode === "create" ? (
                    "Create Partner"
                  ) : (
                    "Update Partner"
                  )}
                </Button>
              </>
            )}
          </ModalFooter>
        </>
      </Modal>

      <LicenseModal
        isOpen={licenseModalState.isOpen}
        toggle={closeLicenseModal}
        mode={licenseModalState.mode}
        licenseData={licenseModalState.licenseData}
        onSubmit={handleLicenseModalSubmit}
      />
    </>
  );
};

export default PartnerModal;
